import { apiFetch } from '../utils/apiFetch';

const getAuthHeaders = () => {
  const stored = localStorage.getItem('authTokens');
  const token = stored ? JSON.parse(stored).access_token : null;
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
};

// Types
export interface PaperAccount {
  account_id: number;
  initial_balance_eur: number;
  total_budget_eur: number;
  created_at: string | null;
  reset_at: string | null;
  balances: Array<{ currency: string; available: number }>;
}

export interface PaperTrade {
  id: number;
  market: string;
  side: 'buy' | 'sell';
  amount: number;
  price: number;
  total: number;
  fee: number;
  status: string;
  strategy_id: number | null;
  realized_pnl: number;
  reason: string | null;
  created_at: string | null;
}

export interface PortfolioValue {
  total_eur: number;
  initial_eur: number;
  pnl_eur: number;
  pnl_percent: number;
  positions: Array<{
    currency: string;
    amount: number;
    value_eur: number;
    price: number;
    price_unavailable?: boolean;
  }>;
  warnings: string[];
}

export interface BudgetOverview {
  total_budget_eur: number;
  allocated_eur: number;
  used_eur: number;
  available_eur: number;
  total_fees_eur: number;
  strategies: Array<{
    id: number;
    name: string;
    strategy_type: string;
    market: string;
    budget_eur: number;
    budget_used_eur: number;
    is_active: boolean;
    interval_seconds: number;
    total_trades: number;
    winning_trades: number;
    total_profit_loss: number;
    stop_loss_percentage: number | null;
    take_profit_percentage: number | null;
    min_trade_amount_eur: number | null;
    min_trade_behavior: 'skip' | 'round_up' | null;
    last_signal: { action: string; reason: string } | null;
    last_signal_at: string | null;
    last_executed: string | null;
  }>;
}

export interface OnboardingRequest {
  total_budget_eur: number;
  strategy_type: string;
  strategy_name: string;
  market: string;
  amount_per_trade: number;
}

// API functions
export const paperTradingApi = {
  getAccount: async (): Promise<PaperAccount> => {
    const res = await apiFetch(`/paper/account`, { headers: getAuthHeaders() });
    if (!res.ok) throw new Error('Failed to fetch account');
    return res.json();
  },

  getPortfolio: async (): Promise<PortfolioValue> => {
    const res = await apiFetch(`/paper/portfolio`, { headers: getAuthHeaders() });
    if (!res.ok) throw new Error('Failed to fetch portfolio');
    return res.json();
  },

  getBudget: async (): Promise<BudgetOverview> => {
    const res = await apiFetch(`/paper/budget`, { headers: getAuthHeaders() });
    if (!res.ok) throw new Error('Failed to fetch budget');
    return res.json();
  },

  updateBudget: async (totalBudgetEur: number): Promise<{ total_budget_eur: number }> => {
    const res = await apiFetch(`/paper/budget`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ total_budget_eur: totalBudgetEur }),
    });
    if (!res.ok) throw new Error('Failed to update budget');
    return res.json();
  },

  placeOrder: async (market: string, side: 'buy' | 'sell', amountEur: number) => {
    const res = await apiFetch(`/paper/order`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ market, side, amount_eur: amountEur }),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.detail || 'Order failed');
    }
    return res.json();
  },

  getTrades: async (limit = 50): Promise<PaperTrade[]> => {
    // Backend liefert seit dem Trade-Log-Refactor ein paginiertes Objekt
    // {trades, total, page, page_size}. Fuer Bestands-Aufrufer (Dashboard
    // PortfolioChart, Activity-Feed) packen wir das Array hier aus.
    // Param heisst neu page_size (limit wird ignoriert) — wir senden
    // beide aus Backward-Compat.
    const res = await apiFetch(`/paper/trades?page_size=${limit}&limit=${limit}`, {
      headers: getAuthHeaders(),
    });
    if (!res.ok) throw new Error('Failed to fetch trades');
    const data = await res.json();
    if (Array.isArray(data)) return data;
    return Array.isArray(data?.trades) ? data.trades : [];
  },

  completeOnboarding: async (data: OnboardingRequest) => {
    const res = await apiFetch(`/paper/onboarding/complete`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error('Onboarding failed');
    return res.json();
  },

  getAvailableStrategies: async () => {
    const res = await apiFetch(`/paper/strategies/available`, { headers: getAuthHeaders() });
    if (!res.ok) throw new Error('Failed to fetch strategies');
    return res.json();
  },
};
